import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Material UI imports
import {
  Box,
  Grid,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Card as MuiCard,
  CardContent,
  Paper,
  Alert,
  Container
} from "@mui/material";
import { Add as AddIcon, Check as CheckIcon } from "@mui/icons-material";

// Context and hooks
import SystemContext from "../../../../../../context/SystemContext";
import useFetch from "../../../../../../hooks/useFetch";

// Constants
import { API_URL_SALES_CARD, API_VINDI, TOKEN_VINDI } from "constants/api";

// Local components
import ExistingCards from './ExistingCards';
import AddCardModal from './AddCardModal';
import NotificationSnackbar from './NotificationSnackbar';

// Utils and constants
import { INITIAL_CARD_STATE } from './cardConstants';
import { formatDocumentNumber, formatCardNumber, formatCardDisplay } from './cardFormatting';
import {
  detectCardType,
  validateDocumentNumber,
  validateCardNumber,
  validateExpiry,
  validateCVV
} from './cardValidation';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CreditCard = ({isBrazil}) => {
  const { t } = useTranslation();
  console.log("CreditCard - isBrazil", isBrazil);

  // Context and state management
  const {
    CheckError,
    setCheckError,
    PurchaseInfo,
    setPurchaseInfo,
    UserCards,
    setUserCards,
    errorRule,
    setErrorRule,
  } = useContext(SystemContext);

  // API hooks for card operations
  const [vindiResponse, isVindiLoading, vindiError, fetchVindiToken] = useFetch({
    autoInit: false,
  });

  const [cardResponse, isCardLoading, cardError, fetchSaveCard] = useFetch({
    autoInit: false,
  });

  // Component state
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isCvvFocused, setIsCvvFocused] = useState(false);
  const [newCreditCard, setNewCreditCard] = useState(INITIAL_CARD_STATE);
  const [isCardAdded, setIsCardAdded] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [newlyAddedCardId, setNewlyAddedCardId] = useState(null);

  // Estado para salvar o método de pagamento anterior
  const [previousMethodChosen, setPreviousMethodChosen] = useState(null);

  // Estado local para gerenciar a lista de cartões (fallback se setUserCards não existir)
  const [localUserCards, setLocalUserCards] = useState(UserCards || []);

  // ============================================================================
  // SNACKBAR STATES
  // ============================================================================
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // ============================================================================
  // SNACKBAR FUNCTIONS
  // ============================================================================

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // ============================================================================
  // VALIDATION FUNCTIONS
  // ============================================================================

  /**
   * Verifica se o cartão tem um token válido do Vindi
   */
  const isCardValid = (card) => {
    return card.vindi_payment_profile_id && card.vindi_payment_profile_id !== null;
  };

  /**
   * Valida se a bandeira do cartão corresponde ao número informado
   */
  const validateCardFlag = (cardNumber, storedFlag) => {
    // Remove espaços e caracteres especiais do número
    const cleanNumber = cardNumber.replace(/[^\d]/g, '');

    // Se o número não tem dígitos suficientes, não pode validar
    if (cleanNumber.length < 6) {
      return { isValid: false, detectedFlag: null };
    }

    // Detecta a bandeira baseada no número
    const detectedFlag = detectCardType(cleanNumber);

    // Verifica se a bandeira detectada corresponde à armazenada
    const isValid = detectedFlag === storedFlag;

    return { isValid, detectedFlag, storedFlag };
  };

  const validateForm = () => {
    const errors = {};

    // Validação do nome do titular
    if (!newCreditCard.card_name || newCreditCard.card_name.trim().length < 2) {
      errors.card_name = 'Nome do titular é obrigatório (mínimo 2 caracteres)';
    } else if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(newCreditCard.card_name.trim())) {
      errors.card_name = 'Nome deve conter apenas letras e espaços';
    } else if (newCreditCard.card_name.trim().split(' ').filter(word => word.length > 0).length < 2) {
      errors.card_name = 'Digite nome e sobrenome';
    }

    // Validação do CPF/CNPJ
    if (isBrazil && !validateDocumentNumber(newCreditCard.registry_code)) {
      errors.registry_code = 'Documento inválido';
    }

    // Validação do número do cartão
    if (!validateCardNumber(newCreditCard.number)) {
      errors.number = 'Número do cartão inválido. Verifique os dados.';
    }

    // Validação da validade
    if (!newCreditCard.expMonth || !newCreditCard.expYear) {
      errors.validade = 'Preencha mês e ano de validade';
    } else if (newCreditCard.expMonth.length < 2) {
      errors.validade = 'Mês deve ter 2 dígitos (01-12)';
    } else if (newCreditCard.expYear.length < 4) {
      errors.validade = 'Ano deve ter 4 dígitos';
    } else if (!validateExpiry(newCreditCard.expMonth, newCreditCard.expYear)) {
      const monthNum = parseInt(newCreditCard.expMonth);
      const yearNum = parseInt(newCreditCard.expYear);
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      if (monthNum < 1 || monthNum > 12) {
        errors.validade = 'Mês deve estar entre 01 e 12';
      } else if (yearNum < currentYear) {
        errors.validade = 'Ano não pode ser anterior ao atual';
      } else if (yearNum > currentYear + 20) {
        errors.validade = 'Validade fora do intervalo aceito.';
      } else if (yearNum === currentYear && monthNum < currentMonth) {
        errors.validade = 'Cartão vencido - verifique a data';
      }
    }

    // Validação do CVV
    if (!validateCVV(newCreditCard.cvv)) {
      errors.cvv = 'CVV deve ter 3 ou 4 dígitos';
    }

    // Validação da bandeira
    if (!newCreditCard.flag) {
      errors.bandeira = 'Selecione a bandeira do cartão';
    }

    return errors;
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const updateCardsList = (newCard) => {
    if (setUserCards && typeof setUserCards === 'function') {
      setUserCards(prevCards => [...(prevCards || []), newCard]);
    } else {
      setLocalUserCards(prevCards => [...prevCards, newCard]);
    }
  };

  const getCurrentCardsList = () => {
    return UserCards || localUserCards;
  };

  /**
   * Verifica se há um cartão válido selecionado
   */
  const hasValidSelectedCard = () => {
    if (selectedCardIndex === null) return false;
    const currentCards = getCurrentCardsList();
    if (!currentCards || currentCards.length === 0) return false;
    const selectedCard = currentCards[selectedCardIndex];
    return isCardValid(selectedCard);
  };

  // ============================================================================
  // RESET FUNCTIONS
  // ============================================================================

  const resetToInitialState = () => {
    setPurchaseInfo((prevState) => ({
      ...prevState,
      MethodChosen: null,
      SuccessState: "initial",
      CreditCard: {},
      OldCards: {}
    }));

    setSelectedCardIndex(null);
    setNewCreditCard(INITIAL_CARD_STATE);
    setFormErrors({});
    setPreviousMethodChosen(null);

    setErrorRule((prevState) => ({
      ...prevState,
      number: false,
      cvvNumber: false,
      expMonth: false,
      expYear: false,
    }));
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleCardInputChange = (field, value) => {
    let formattedValue = value;

    if (field === 'registry_code') {
      formattedValue = formatDocumentNumber(value);
    } else if (field === 'number') {
      formattedValue = formatCardNumber(value);
      const cleanNumber = value.replace(/\D/g, '');
      const detectedType = detectCardType(cleanNumber);
      if (detectedType) {
        setNewCreditCard(prev => ({ ...prev, flag: detectedType }));
        showSnackbar(`Bandeira ${detectedType} detectada automaticamente!`, 'info');
      }
    } else if (field === 'card_name') {
      const originalValue = value;
      formattedValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, '');

      if (originalValue !== formattedValue) {

        showSnackbar('Apenas letras são permitidas no nome do titular', 'warning');
      }
    } else if (field === 'expMonth') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length === 2) {
        const monthNum = parseInt(formattedValue);
        if (monthNum > 12) {
          formattedValue = '12';
        } else if (monthNum < 1) {
          formattedValue = '01';
        }
      }
    } else if (field === 'expYear') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length === 4) {
        const yearNum = parseInt(formattedValue);
        const currentYear = new Date().getFullYear();
        if (yearNum < currentYear) {
          formattedValue = currentYear.toString();
        }
      }
    } else if (field === 'cvv') {
      formattedValue = value.replace(/\D/g, '');
    }

    setNewCreditCard(prev => ({
      ...prev,
      [field]: formattedValue
    }));

    setPurchaseInfo(prevState => ({
      ...prevState,
      SuccessState: "processing",
      CreditCard: {
        ...prevState.CreditCard,
        [field]: formattedValue,
      },
    }));

    if (formErrors[field] || formErrors.validade) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
        validade: field === 'expMonth' || field === 'expYear' ? '' : prev.validade
      }));
    }
  };

  const handleSelectCardBrand = (brandName) => {
    setNewCreditCard(prev => ({
      ...prev,
      flag: brandName
    }));

    if (formErrors.bandeira) {
      setFormErrors(prev => ({
        ...prev,
        bandeira: ''
      }));
    }

    showSnackbar(`Bandeira ${brandName} selecionada!`, 'success');
  };

  // ============================================================================
  // FUNÇÃO ATUALIZADA - COM VALIDAÇÃO DE TOKEN DO VINDI
  // ============================================================================
  const handleExistingCardSelection = (cardId, index) => {
    const selectedCard = getCurrentCardsList()[index];

    // Verificar se o cartão tem token válido do Vindi
    if (!isCardValid(selectedCard)) {
      showSnackbar('Este cartão não possui token válido e não pode ser utilizado', 'error');
      return;
    }

    setSelectedCardIndex(index);

    setPurchaseInfo((prevState) => ({
      ...prevState,
      MethodChosen: "OldCards",
      SuccessState: "processing",
      OldCards: {
        ...prevState.OldCards,
        card_id: cardId,
      },
    }));

    setErrorRule((prevState) => ({
      ...prevState,
      number: false,
      cvvNumber: false,
      expMonth: false,
      expYear: false,
    }));

    showSnackbar(`Cartão ${selectedCard.flag} selecionado com sucesso!`, 'success');
  };

  const handleTermsChange = (event) => {
    const isChecked = event.target.checked;
    setIsTermsAccepted(isChecked);

    if (isChecked) {
      showSnackbar('Termos de uso aceitos!', 'success');
    } else {
      showSnackbar('Você precisa aceitar os termos para continuar', 'warning');
    }
  };

  // ============================================================================
  // FUNÇÃO CORRIGIDA - handleOpenNewCardModal
  // ============================================================================
  const handleOpenNewCardModal = () => {
    // Salvar o método atual antes de alterar
    setPreviousMethodChosen(PurchaseInfo.MethodChosen);

    setSelectedCardIndex(null);
    setFormErrors({});

    // Definir método como CreditCard apenas para o modal
    setPurchaseInfo((prevState) => ({
      ...prevState,
      MethodChosen: "CreditCard",
      SuccessState: "processing"
    }));

    setIsModalOpen(true);
  };

  // ============================================================================
  // FUNÇÃO CORRIGIDA - handleCloseModal
  // ============================================================================
  const handleCloseModal = () => {
    // Restaurar o método anterior se existir
    if (previousMethodChosen !== null) {
      setPurchaseInfo((prevState) => ({
        ...prevState,
        MethodChosen: previousMethodChosen,
        SuccessState: previousMethodChosen === "OldCards" ? "processing" : prevState.SuccessState
      }));
    } else {
      // Se não havia método anterior, limpar completamente
      setPurchaseInfo((prevState) => ({
        ...prevState,
        MethodChosen: null,
        SuccessState: "initial"
      }));
    }

    // Limpar estado do modal
    setCheckError((prevState) => ({
      ...prevState,
      creditCardShowError: false,
    }));

    setIsCardAdded(false);
    setIsModalOpen(false);
    setFormErrors({});
    setNewCreditCard(INITIAL_CARD_STATE);
    setPreviousMethodChosen(null); // Limpar o estado salvo

    // Resetar regras de erro para evitar validações indesejadas
    setErrorRule((prevState) => ({
      ...prevState,
      number: false,
      cvvNumber: false,
      expMonth: false,
      expYear: false,
    }));
  };

  // ============================================================================
  // API FUNCTIONS
  // ============================================================================

  const tokenizeCard = async () => {
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      // Validar bandeira APENAS para novos cartões
      const cleanNumber = newCreditCard.number.replace(/\s/g, '');
      const flagValidation = validateCardFlag(cleanNumber, newCreditCard.flag);

      if (!flagValidation.isValid) {
        showSnackbar('Bandeira incorreta do seu cartão', 'error');
        return;
      }

      showSnackbar('Processando seu cartão...', 'info');

      const cardData = {
        card_number: newCreditCard.number.replace(/\s/g, ''),
        card_expiration: `${newCreditCard.expMonth}/${newCreditCard.expYear}`,
        card_cvv: newCreditCard.cvv,
        registry_code: newCreditCard.registry_code.replace(/\D/g, ''),
        holder_name: newCreditCard.card_name,
        payment_method_code: "credit_card",
        payment_company_code: newCreditCard.flag.toLowerCase()
      };

      fetchVindiToken({
        url: API_VINDI,
        header: {
          "Content-Type": "application/json",
          'Access-Control-Allow-Origin': '*',
          'Authorization': `Basic ${TOKEN_VINDI}`,
        },
        data: cardData,
        method: "POST",
        noAuth: true
      });
    } else {
      const errorCount = Object.keys(errors).length;
      showSnackbar(`Por favor, corrija ${errorCount} ${errorCount === 1 ? 'erro' : 'erros'} no formulário`, 'error');
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (UserCards) {
      setLocalUserCards(UserCards);
    }
  }, [UserCards]);

  // Selecione automaticamente o último cartão válido no carregamento inicial ou quando a lista de cartões for alterada
  useEffect(() => {
    const currentCards = getCurrentCardsList();

    // OSelecione automaticamente somente se nenhum cartão estiver selecionado no momento e nenhum cartão estiver sendo adicionado recentemente
    if (currentCards && currentCards.length > 0 &&
      selectedCardIndex === null &&
      !newlyAddedCardId) {

      // Encontre o último cartão válido (itere para trás a partir do final)
      for (let i = currentCards.length - 1; i >= 0; i--) {
        const card = currentCards[i];
        if (isCardValid(card)) {
          handleExistingCardSelection(card.id, i);
          break;
        }
      }
    }
  }, [UserCards, localUserCards]);

  useEffect(() => {
    if (vindiError) {
      // Verificar se é erro de bandeira
      const errorMessage = vindiError?.message || vindiError?.toString() || '';
      const isFlagError = errorMessage.toLowerCase().includes('bandeira') ||
        errorMessage.toLowerCase().includes('flag') ||
        errorMessage.toLowerCase().includes('card type') ||
        errorMessage.toLowerCase().includes('payment_company_code');

      if (isFlagError) {
        showSnackbar('Bandeira incorreta do seu cartão', 'error');
      } else {
        showSnackbar('Erro ao processar cartão. Verifique os dados e tente novamente.', 'error');
      }
    }
  }, [vindiError]);

  useEffect(() => {
    if (cardError) {
      showSnackbar('Erro ao salvar cartão. Tente novamente.', 'error');
    }
  }, [cardError]);

  useEffect(() => {
    if (PurchaseInfo.MethodChosen === "CreditCard") {
      const errors = validateForm();
      setErrorRule((prevState) => ({
        ...prevState,
        number: !!errors.number,
        cvvNumber: !!errors.cvv,
        expMonth: !!errors.validade,
        expYear: !!errors.validade,
        checkbox: isTermsAccepted,
      }));
    } else if (PurchaseInfo.MethodChosen === "OldCards") {
      setErrorRule((prevState) => ({
        ...prevState,
        checkbox: isTermsAccepted,
      }));
    }
  }, [PurchaseInfo, isTermsAccepted, newCreditCard]);

  useEffect(() => {
    const hasNoErrors = Object.keys(errorRule).every(
      (key) => errorRule[key] === false || key === "checkbox"
    );

    setCheckError((prevState) => ({
      ...prevState,
      creditCard: hasNoErrors,
    }));
  }, [errorRule, isModalOpen]);

  useEffect(() => {
    setIsCvvFocused(newCreditCard.cvv?.length > 0);
  }, [newCreditCard.cvv]);

  useEffect(() => {
    if (vindiResponse) {

      const cleanCardNumber = newCreditCard.number.replace(/\s/g, '');

      const cardToSave = {
        token: vindiResponse.payment_profile.gateway_token,
        flag: newCreditCard.flag,
        masked_number: formatCardDisplay(cleanCardNumber),
        holder_name: newCreditCard.card_name,
        registry_code: newCreditCard.registry_code.replace(/\D/g, ''),
        customer_id: PurchaseInfo.customer_id,
        type: "creditcard"
      };


      fetchSaveCard({
        url: API_URL_SALES_CARD,
        header: {
          "Content-Type": "application/json",
          'Origin': '*'
        },
        data: cardToSave,
        method: "POST"
      });
    }
  }, [vindiResponse]);

  useEffect(() => {
    if (cardResponse) {

      const cleanCardNumber = newCreditCard.number.replace(/\s/g, '');

      const newCardForList = {
        id: cardResponse.id,
        flag: newCreditCard.flag,
        masked_number: formatCardDisplay(cleanCardNumber),
        holder_name: newCreditCard.card_name,
        registry_code: newCreditCard.registry_code.replace(/\D/g, ''),
        type: "creditcard",
        ...cardResponse
      };



      updateCardsList(newCardForList);

      setNewlyAddedCardId(cardResponse.id);

      setPurchaseInfo((prevState) => ({
        ...prevState,
        MethodChosen: "OldCards",
        SuccessState: "processing",
        OldCards: {
          ...prevState.OldCards,
          card_id: cardResponse.id,
        },
      }));

      setIsCardAdded(true);
      setCheckError((prevState) => ({
        ...prevState,
        creditCardShowError: true,
      }));

      setNewCreditCard(INITIAL_CARD_STATE);
      setFormErrors({});
      setIsModalOpen(false);
      setPreviousMethodChosen(null); // Limpar após sucesso

      showSnackbar(`Cartão ${newCreditCard.flag} adicionado e selecionado com sucesso!`, 'success');

    }
  }, [cardResponse]);

  useEffect(() => {
    if (newlyAddedCardId) {
      const currentCards = getCurrentCardsList();
      if (currentCards && currentCards.length > 0) {
        const cardIndex = currentCards.findIndex(card => card.id === newlyAddedCardId);

        if (cardIndex !== -1) {
          setSelectedCardIndex(cardIndex);

          setTimeout(() => {
            setNewlyAddedCardId(null);
          }, 3000);
        }
      }
    }
  }, [localUserCards, UserCards, newlyAddedCardId]);

  useEffect(() => {
    if (CheckError.creditCardShowError && !isModalOpen &&
      (CheckError.creditCard || !isTermsAccepted)) {
      const errorElement = document.getElementById("errorCheckbox");
      errorElement?.scrollIntoView({ behavior: "smooth" });
    }
  }, [CheckError, isModalOpen, isTermsAccepted]);

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderAddCardButton = () => (
    <Grid item xs={12} sm={6}>
      <Paper
        sx={{
          p: 2,
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isCardAdded ? 'success.main' : 'grey.400',
          bgcolor: isCardAdded ? 'success.50' : 'transparent'
        }}
        onClick={handleOpenNewCardModal}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          color: isCardAdded ? 'success.main' : 'text.secondary'
        }}>
          {isCardAdded ? <CheckIcon /> : <AddIcon />}
          <Typography
            data-testid={`${isCardAdded ? 'payment_card_added_text' : 'payment_addNewCard_text'}`}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              lineHeight: { xs: 1.3, sm: 1.4 }
            }}
          >
            {isCardAdded ? t("purchase.card_added") : t("purchase.add_new_card")}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );

  // ============================================================================
  // FUNÇÃO PARA VERIFICAR SE HÁ CARTÕES VÁLIDOS
  // ============================================================================
  const hasValidCards = () => {
    const cards = getCurrentCardsList();
    return cards && cards.length > 0 && cards.some(card => isCardValid(card));
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (getCurrentCardsList() && getCurrentCardsList().length > 0) {
    getCurrentCardsList().forEach((card, index) => {
    });
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 2, md: 3, lg: 4 } }}>
      <AddCardModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        cardData={newCreditCard}
        formErrors={formErrors}
        onInputChange={handleCardInputChange}
        onBrandSelect={handleSelectCardBrand}
        onSubmit={tokenizeCard}
        isLoading={isVindiLoading || isCardLoading}
        isCvvFocused={isCvvFocused}
        setIsCvvFocused={setIsCvvFocused}
      />

      <NotificationSnackbar
        snackbar={snackbar}
        onClose={handleCloseSnackbar}
      />

      <Box sx={{
        maxWidth: { xs: '100%', sm: 800, md: 900, lg: 1000 },
        mx: 'auto',
        px: { xs: 0.5, sm: 1, md: 2 }
      }}>
        <MuiCard>
          <CardContent sx={{
            p: { xs: 1.5, sm: 2, md: 3, lg: 4 },
            '&:last-child': { pb: { xs: 1.5, sm: 2, md: 3, lg: 4 } }
          }}>
            <Box sx={{
              textAlign: 'center',
              mb: { xs: 3, sm: 4, md: 5 }
            }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem', lg: '1.5rem' },
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.2,
                  mb: 1
                }}
              >
                {t("purchase.select_your_card_or_enter_a_new_one")}
              </Typography>
            </Box>

            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Grid container spacing={2}>
                {getCurrentCardsList()?.length > 0 && (
                  <Grid item xs={12}>
                    <ExistingCards
                      cards={getCurrentCardsList()}
                      selectedCardIndex={selectedCardIndex}
                      onCardSelection={handleExistingCardSelection}
                      newlyAddedCardId={newlyAddedCardId}
                    />
                  </Grid>
                )}

                {renderAddCardButton()}
              </Grid>
            </Box>

            {/* Alerta se não há cartões válidos */}
            {getCurrentCardsList()?.length > 0 && !hasValidCards() && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                    lineHeight: { xs: 1.4, sm: 1.5 }
                  }}
                >
                  <strong>Atenção:</strong>  Os cartões salvos não estão disponíveis para uso devido à atualização do nosso sistema de pagamento. Para continuar, cadastre o cartão novamente.
                </Typography>
              </Alert>
            )}

            {/* Important notice */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                  lineHeight: { xs: 1.4, sm: 1.5 }
                }}
              >
                <strong>{t("purchase.important")}</strong>{" "}
                {t("purchase.billing_occurs_automatically")}
              </Typography>
            </Alert>

            {/* Terms and conditions */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="payment_termsCheckbox_span"
                    checked={isTermsAccepted}
                    onChange={handleTermsChange}
                    disabled={!hasValidSelectedCard()}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                      lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 }
                    }}
                  >
                    {t("purchase.i_declare_that_i_have_read_and_accept_the")}{" "}
                    <a
                      href="https://termos.probrain.com.br/pub/terms/terms_2022.pdf"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#6B46C1', textDecoration: 'underline' }}
                    >
                      {t("purchase.terms_of_use")}
                    </a>
                    {" "}
                    {t("purchase.and_the")}
                    {" "}
                    <a
                      href="https://termos.probrain.com.br/pub/policies/policies_2022.pdf"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#6B46C1', textDecoration: 'underline' }}
                    >
                      {t("purchase.privacy_policy")}
                    </a>
                    .
                  </Typography>
                }
              />

              {/* Error messages */}
              <Box id="errorCheckbox" sx={{ mt: 1 }}>
                {CheckError.creditCardShowError && !isModalOpen && !isTermsAccepted && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                        lineHeight: { xs: 1.4, sm: 1.5 }
                      }}
                    >
                      {t("purchase.mark_the_acceptance_form")}
                    </Typography>
                  </Alert>
                )}

                {CheckError.creditCardShowError && !isModalOpen && !CheckError.creditCard && (
                  <Alert severity="error">
                    <Typography
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                        lineHeight: { xs: 1.4, sm: 1.5 }
                      }}
                    >
                      {t("purchase.please_select_your_credit_card_or_register_one")}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Box>
          </CardContent>
        </MuiCard>
      </Box>
    </Container>
  );
};

export default CreditCard;